import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | done

  const setField = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((x) => ({ ...x, [k]: null }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Enter a valid email";
    if (!form.message.trim() || form.message.trim().length < 10)
      errs.message = "Tell us a bit more (min. 10 characters)";
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        message: form.message.trim(),
      });
      setStatus("done");
      toast.success("Message sent. We'll be in touch.");
    } catch (err) {
      setStatus("idle");
      toast.error(err?.response?.data?.detail || "Could not send. Please retry.");
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative px-4 md:px-6 py-10"
    >
      <div className="relative mx-auto max-w-7xl rounded-[28px] bg-[#0A0A0A] text-white overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.06) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
            maskImage: "radial-gradient(ellipse at 50% 30%, black 20%, transparent 70%)",
            WebkitMaskImage: "radial-gradient(ellipse at 50% 30%, black 20%, transparent 70%)",
          }}
        />

        <div className="relative px-6 md:px-16 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6"
          >
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-white/50">
              <span className="h-px w-8 bg-white/40" />
              Talk to us
            </div>
            <h2
              data-testid="contact-headline"
              className="mt-5 text-5xl sm:text-6xl lg:text-7xl tracking-[-0.04em] font-medium leading-[0.98]"
            >
              Pilot Fittin with{" "}
              <span className="italic font-light text-white/60">your catalog.</span>
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-white/60 max-w-md">
              30-day paid pilots, usually onboarded in under a week. Tell us about your
              store — we'll reply within one business day.
            </p>

            <div className="mt-10 space-y-3 text-[13px] text-white/60">
              <div className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-emerald-400" />
                Dedicated onboarding engineer
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-emerald-400" />
                Return-rate baseline + live A/B dashboard
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-emerald-400" />
                SLA-backed uptime, SSO on request
              </div>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            onSubmit={submit}
            noValidate
            data-testid="contact-form"
            className="lg:col-span-6 w-full"
          >
            {status === "done" ? (
              <div
                data-testid="contact-success"
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-8"
              >
                <div className="flex items-center gap-3">
                  <span className="h-8 w-8 rounded-full bg-emerald-500/15 border border-emerald-500/30 grid place-items-center">
                    <Check className="h-4 w-4 text-emerald-400" />
                  </span>
                  <div className="text-[15px] font-medium">Message sent.</div>
                </div>
                <p className="mt-2 text-[13px] text-white/50">
                  We'll reach out within one business day.
                </p>
              </div>
            ) : (
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 space-y-5">
                <Field
                  label="Name"
                  value={form.name}
                  onChange={setField("name")}
                  testid="contact-name"
                  error={errors.name}
                  placeholder="Jane Doe"
                />
                <Field
                  label="Work email"
                  value={form.email}
                  onChange={setField("email")}
                  testid="contact-email"
                  error={errors.email}
                  type="email"
                  placeholder="jane@brand.com"
                />
                <Field
                  label="Company"
                  value={form.company}
                  onChange={setField("company")}
                  testid="contact-company"
                  placeholder="Ateliér"
                />
                <div>
                  <label className="text-[11px] uppercase tracking-[0.22em] text-white/40">
                    Message
                  </label>
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={setField("message")}
                    data-testid="contact-message"
                    placeholder="Catalog size, markets, current return rate…"
                    className="mt-1 w-full resize-none bg-transparent text-[15px] placeholder:text-white/30 text-white border border-white/15 rounded-xl focus:border-white/70 transition-colors p-3"
                  />
                  {errors.message && (
                    <div
                      data-testid="contact-message-error"
                      className="mt-1 text-[12px] text-red-300"
                    >
                      {errors.message}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={status === "loading"}
                  data-testid="contact-submit"
                  className="mt-2 group w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#0A0A0A] text-[14px] px-5 py-3 hover:bg-neutral-200 transition-all active:scale-[0.99] disabled:opacity-60"
                >
                  {status === "loading" ? (
                    <>
                      <span className="h-3 w-3 rounded-full border-2 border-black/30 border-t-black animate-spin" />
                      Sending
                    </>
                  ) : (
                    <>
                      Send message
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </>
                  )}
                </button>
                <div className="text-[11px] text-white/40">
                  By submitting you agree to be contacted about Fittin.
                </div>
              </div>
            )}
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, testid, error, type = "text", placeholder }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.22em] text-white/40">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        data-testid={testid}
        placeholder={placeholder}
        className="mt-1 w-full bg-transparent text-[16px] placeholder:text-white/30 text-white border-b border-white/15 focus:border-white/70 transition-colors py-2"
      />
      {error && (
        <div
          data-testid={`${testid}-error`}
          className="mt-1 text-[12px] text-red-300"
        >
          {error}
        </div>
      )}
    </div>
  );
}
